import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function WaterHero() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', zip: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => body.append(k, v));
    try {
      await fetch('https://readdy.ai/api/form/d724u53k1jkj467r5rog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <section className="relative min-h-[680px] flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e00cb8436b08ae1c6b7fc777c89716b7.jpg"
          alt="Clean water purification"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/30"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-sky-400 text-sm font-medium mb-6 hover:text-sky-300 cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              {t('wh_back')}
            </Link>
            <span className="bg-sky-500/20 text-sky-400 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border border-sky-500/30 inline-block mb-5">
              {t('wh_badge')}
            </span>
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight mb-5">
              {t('wh_title1')}<br />
              <span className="text-sky-400">{t('wh_title2')}</span>
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-lg">
              {t('wh_desc')}
            </p>
            <div className="flex flex-wrap gap-4">
              {(['wh_feature1', 'wh_feature2', 'wh_feature3'] as const).map((key) => (
                <div key={key} className="flex items-center gap-2 text-white/80 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-sky-400 text-base"></i>
                  </div>
                  {t(key)}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8">
            <h3 className="text-xl font-black text-slate-800 mb-1">{t('wh_form_title')}</h3>
            <p className="text-slate-500 text-sm mb-6">{t('wh_form_sub')}</p>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-sky-600 text-3xl"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">{t('wh_success_title')}</h4>
                <p className="text-slate-500 text-sm">{t('wh_success_desc')}</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('wh_form_name')}</label>
                    <input name="name" type="text" required placeholder={t('wh_form_placeholder_name')} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('wh_form_phone')}</label>
                    <input name="phone" type="tel" required placeholder={t('wh_form_placeholder_phone')} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('wh_form_email')}</label>
                  <input name="email" type="email" required placeholder={t('wh_form_placeholder_email')} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('wh_form_zip')}</label>
                  <input name="zip" type="text" placeholder={t('wh_form_placeholder_zip')} value={formData.zip} onChange={e => setFormData({ ...formData, zip: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('wh_form_message')}</label>
                  <textarea name="message" rows={3} placeholder={t('wh_form_placeholder_msg')} value={formData.message} onChange={e => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 resize-none"></textarea>
                  <p className="text-xs text-slate-400 text-right mt-1">{formData.message.length}/500</p>
                </div>
                <button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-drop-line text-lg"></i>
                  </div>
                  {t('wh_form_submit')}
                </button>
                <p className="text-xs text-slate-400 text-center">{t('wh_form_privacy')}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
